/**
 * Разбор тела раздела на блоки.
 *
 * Чистая функция, отдельно от экрана, по одной причине: она уже сломалась.
 * 14 сентября 2026 внутренний цикл, собиравший строки спецификации картинки,
 * не двигал счётчик строк — страница уходила в вечный цикл и роняла всё
 * приложение на `RangeError: Invalid array length`. Ни `tsc`, ни тесты этого
 * не видели, потому что разбор жил внутри компонента и вызвать его было
 * неоткуда. Теперь его зовёт тест.
 *
 * Разметки ровно столько, сколько нужно четырём разделам, и ни строкой больше:
 * абзац, пункт списка с «- », жирный фрагмент и блок ```numline. Полноценный
 * markdown втащил бы в бандл разборщик ради возможностей, которых в тексте
 * нет, а каждая лишняя возможность — ещё один способ у страницы выглядеть не
 * так, как соседняя.
 */

export type Block =
  | { kind: "p"; text: string }
  /**
   * Абзац, который целиком состоит из одной формулы. Отдельный вид, а не
   * абзац с формулой внутри: в тексте он стоит как объект, вынесенный из
   * строки, и оформляется соответственно.
   *
   * `vars` отличает правило от вычисления. Признак — буквенная переменная:
   * «(√a−√b)(√a+√b) = a−b» верно для любых a и b, а «(√5+√2)² = 5+2√10+2» —
   * один посчитанный шаг примера. Правило обводится рамкой, вычисление
   * заливается, как и выражение внутри строки.
   */
  | { kind: "display"; tex: string; vars: boolean }
  | { kind: "list"; items: string[] }
  | { kind: "numline"; text: string }
  | { kind: "figure"; text: string };

/**
 * Есть ли в формуле буквенная переменная.
 *
 * Имена команд из проверки выбрасываются: буквы в `\sqrt`, `\cdot`, `\frac`
 * набраны тем же алфавитом, но переменными не являются, и без этого шага
 * «выражением» не оказалось бы вообще ничего.
 *
 * Кириллица в `\text{при}` переменной тоже не считается — проверяется только
 * латиница.
 */
export function hasVariable(tex: string): boolean {
  return /[A-Za-z]/.test(tex.replace(/\\[a-zA-Z]+/g, ""));
}

/** Абзац из одной формулы — или `null`, если в нём есть что-то ещё. */
function displayOf(text: string): Block | null {
  const bare = text.trim().replace(/^\*\*/, "").replace(/\*\*$/, "").trim();
  const match = /^\\\((.+)\\\)$/s.exec(bare);
  if (match === null) return null;
  const tex = match[1]!;
  // Жадная звёздочка выше склеила бы «\(a\) и \(b\)» в одну формулу с текстом
  // внутри. Открывающая или закрывающая скобка посреди — признак того, что
  // формул было две, а между ними стояли слова.
  if (/\\[()]/.test(tex)) return null;
  return { kind: "display", tex, vars: hasVariable(tex) };
}

export function blocksOf(source: string): Block[] {
  const out: Block[] = [];
  const lines = source.split("\n");
  let i = 0;

  while (i < lines.length) {
    const line = lines[i]!;
    if (line.trim() === "") {
      i++;
      continue;
    }
    if (line.startsWith("```")) {
      // Вид блока проверяет сборка (`tools/export_theory.py`), поэтому сюда
      // доезжает только `numline`. Съедаем до закрывающей ограды в любом
      // случае: неизвестный вид лучше пропустить, чем встать на нём навсегда.
      const numline = line.startsWith("```numline");
      const figure = line.startsWith("```figure");
      const spec: string[] = [];
      i++;
      while (i < lines.length && !lines[i]!.startsWith("```")) {
        spec.push(lines[i]!);
        i++;
      }
      i++;
      if (numline) out.push({ kind: "numline", text: spec.join(" ").trim() });
      // Чертёж приезжает готовой спецификацией: имя в неё превратил
      // tools/export_theory.py. Строки склеиваются переводом, а не пробелом —
      // это JSON, и пробел внутри строкового поля был бы правкой текста.
      if (figure) out.push({ kind: "figure", text: spec.join("\n").trim() });
      continue;
    }
    if (line.startsWith("- ")) {
      const items: string[] = [];
      while (i < lines.length && lines[i]!.startsWith("- ")) {
        items.push(lines[i]!.slice(2));
        i++;
      }
      out.push({ kind: "list", items });
      continue;
    }
    const paragraph: string[] = [];
    while (
      i < lines.length &&
      lines[i]!.trim() !== "" &&
      !lines[i]!.startsWith("```") &&
      !lines[i]!.startsWith("- ")
    ) {
      paragraph.push(lines[i]!);
      i++;
    }
    // Страховка от того же дефекта в будущем: если ни одна ветка строку не
    // съела, двигаемся принудительно. Вечный цикл здесь роняет приложение
    // целиком, а пропущенная строка — это одна пропущенная строка.
    if (paragraph.length === 0) {
      i++;
      continue;
    }
    const text = paragraph.join(" ");
    out.push(displayOf(text) ?? { kind: "p", text });
  }
  return out;
}
